import React from "react";
import { Link } from "react-router-dom";
import "./PartnerPage.css";

const partnerBenefits = [
  {
    number: "01",
    title: "Reach teams actively planning",
    description:
      "Be considered when managers, HR teams, and executive assistants are searching for experiences that match your location, capacity, and offering.",
  },
  {
    number: "02",
    title: "Control how you are presented",
    description:
      "Review your business description, images, group-size information, pricing guidance, and other details shown through Teamward.",
  },
  {
    number: "03",
    title: "Receive better-informed inquiries",
    description:
      "Teamward gathers information such as group size, budget, preferred dates, and event goals before coordinating with you.",
  },
];

const preferredPartnerFeatures = [
  "A complete, partner-approved business profile",
  "Teamward Preferred Partner identification",
  "Consideration for personalized recommendations",
  "Opportunities to participate in curated event packages",
  "Referral-based company booking inquiries",
  "The ability to update listing details as your offering changes",
];

const partnerControls = [
  "You approve your own availability and final pricing",
  "You may accept or decline individual inquiries",
  "No booking is confirmed without your approval",
  "The relationship is nonexclusive",
  "Referral terms are agreed upon before activation",
];

const partnerSteps = [
  {
    number: "01",
    title: "Tell us about your business",
    description:
      "Share your services, locations, group capacity, pricing guidance, images, and preferred contact information.",
  },
  {
    number: "02",
    title: "We review the partnership fit",
    description:
      "Teamward reviews your offering and discusses listing details, referral terms, and opportunities that may be relevant to your business.",
  },
  {
    number: "03",
    title: "Your Preferred Partner profile goes live",
    description:
      "Once the details are approved, your business may be featured in recommendations, booking inquiries, and customizable Teamward packages.",
  },
];

const PartnerPage = () => {
  return (
    <main className="partner-page">
      {/* HERO */}
      <section className="partner-hero">
        <div className="partner-hero-inner">
          <div className="partner-hero-copy">
            <p className="partner-eyebrow">
              Become a Preferred Partner
            </p>

            <h1>
              Put your business in front of teams ready to plan.
            </h1>

            <p className="partner-hero-description">
              Teamward helps managers, HR teams, and executive assistants
              discover company experiences that fit their teams. We’re inviting
              a select group of Casper and Denver businesses to join our
              Preferred Partner network.
            </p>

            <div className="partner-hero-actions">
              <Link
                to="/partner-application"
                className="partner-button partner-button-primary"
              >
                Apply to Become a Preferred Partner
              </Link>
            </div>
          </div>

          <aside className="partner-hero-card">
            <p className="partner-card-eyebrow">
              Launch Partner Opportunity
            </p>

            <h2>No upfront listing fee.</h2>

            <p className="partner-hero-card-copy">
              Approved partnerships are referral-based. Teamward receives an
              agreed commission only when an attributed booking is completed.
            </p>

            <div className="partner-hero-card-details">
              <div>
                <small>Initial markets</small>
                <strong>Casper + Denver</strong>
              </div>

              <div>
                <small>Relationship</small>
                <strong>Nonexclusive</strong>
              </div>

              <div>
                <small>Application</small>
                <strong>Free</strong>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* BENEFITS */}
      <section
        id="preferred-partner-details"
        className="partner-benefits-section"
      >
        <div className="partner-section-inner">
          <div className="partner-section-heading">
            <p className="partner-eyebrow">
              Why Become a Preferred Partner?
            </p>

            <h2>More than a basic directory listing.</h2>

            <p>
              Preferred Partners receive a more complete presence on Teamward
              and can be considered for qualified inquiries, personalized
              recommendations, and curated event packages.
            </p>
          </div>

          <div className="partner-benefits-grid">
            {partnerBenefits.map((benefit) => (
              <article
                className="partner-benefit-card"
                key={benefit.number}
              >
                <span className="partner-benefit-number">
                  {benefit.number}
                </span>

                <h3>{benefit.title}</h3>

                <p>{benefit.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUDED / CONTROL */}
      <section className="partner-includes-section">
        <div className="partner-section-inner">
          <div className="partner-section-heading">
            <p className="partner-eyebrow">
              Preferred Partner Benefits
            </p>

            <h2>
              A stronger presence with clear partner control.
            </h2>

            <p>
              Teamward is designed to help qualified businesses reach company
              groups without taking control away from the provider.
            </p>
          </div>

          <div className="partner-includes-grid">
            <article className="partner-list-card">
              <p className="partner-list-label">
                Preferred Partner status can include
              </p>

              <ul>
                {preferredPartnerFeatures.map((feature) => (
                  <li key={feature}>
                    <span
                      className="partner-check"
                      aria-hidden="true"
                    >
                      ✓
                    </span>

                    <p>{feature}</p>
                  </li>
                ))}
              </ul>
            </article>

            <article className="partner-list-card partner-control-card">
              <p className="partner-list-label">
                You remain in control
              </p>

              <ul>
                {partnerControls.map((control) => (
                  <li key={control}>
                    <span
                      className="partner-check"
                      aria-hidden="true"
                    >
                      ✓
                    </span>

                    <p>{control}</p>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="partner-process-section">
        <div className="partner-section-inner">
          <div className="partner-section-heading">
            <p className="partner-eyebrow">
              How It Works
            </p>

            <h2>Getting started is straightforward.</h2>

            <p>
              A simple process from application to an approved Teamward
              presence.
            </p>
          </div>

          <div className="partner-process-grid">
            {partnerSteps.map((step) => (
              <article
                className="partner-process-step"
                key={step.number}
              >
                <span className="partner-process-number">
                  {step.number}
                </span>

                <h3>{step.title}</h3>

                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* REFERRAL NOTE */}
      <section className="partner-note-section">
        <div className="partner-section-inner">
          <div className="partner-referral-note">
            <div
              className="partner-referral-icon"
              aria-hidden="true"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 4h6.2c.53 0 1.04.21 1.41.59l8.8 8.8a2 2 0 0 1 0 2.82l-4.2 4.2a2 2 0 0 1-2.82 0l-8.8-8.8A2 2 0 0 1 4 10.2V4Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                <circle
                  cx="8"
                  cy="8"
                  r="1.25"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
              </svg>
            </div>

            <div className="partner-referral-copy">
              <h2>No upfront application or listing fee.</h2>

              <p>
                Approved Preferred Partners agree to referral terms before the
                partnership begins. Any commission applies only to qualifying,
                attributed bookings under the agreed partner terms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="partner-cta-wrapper">
        <div className="partner-section-inner">
          <div className="partner-cta-section">
            <p className="partner-cta-eyebrow">
              Partner With Teamward
            </p>

            <h2>
              Ready to become a
              <br />
              Preferred Partner?
            </h2>

            <p>
              Tell us about your business and the company experiences you
              provide. We’ll review the information and contact you about
              eligibility and next steps.
            </p>

            <Link
              to="/partner-application"
              className="partner-button partner-button-primary"
            >
              Apply to Become a Preferred Partner
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PartnerPage;
